import { SyntheticEvent, useEffect, useRef } from "react";
import { Video } from "@videojs/react/video";
import { GoogleCast } from "@videojs/react/media/google-cast";
import { useTranslation } from "react-i18next";

import { player } from "./Player";
import { VideoProps } from "../../types/VideoTypes";
import VideoPlayerSkin from "./VideoPlayerSkin";
import CaptionTrack from "./CaptionTrack";
import {
  createAnalyticsSession,
  handleSessionPlay,
  handleSessionTimeUpdate,
  sendVideoAnalyticsEvent,
} from "../../utils/videoTracking";

export default function VideoPlayer(props: Readonly<VideoProps>) {
  const { video, title, poster, captions, chapters } = props;
  const { t } = useTranslation();
  const analyticsSessionRef = useRef(
    video ? createAnalyticsSession(video.id, title) : undefined
  );

  // Sends the "video" analytics event once the user actually leaves the page
  // (browser navigation, reload, tab close, or in-app navigation that
  // unmounts this component). `pagehide`/`beforeunload` are used instead of
  // `visibilitychange`, since a plain tab switch also fires `visibilitychange`
  // but should not send the event yet — the user may switch back and keep watching.
  useEffect(() => {
    const sendAnalyticsEvent = () => {
      if (!analyticsSessionRef.current) return;

      analyticsSessionRef.current = sendVideoAnalyticsEvent(
        analyticsSessionRef.current
      );
    };

    // pagehide is used when navigating away from the page.
    window.addEventListener("pagehide", sendAnalyticsEvent);
    // beforeunload is used when the user reloads the current page.
    window.addEventListener("beforeunload", sendAnalyticsEvent);

    return () => {
      window.removeEventListener("pagehide", sendAnalyticsEvent);
      window.removeEventListener("beforeunload", sendAnalyticsEvent);
      sendAnalyticsEvent();
    };
  }, []);

  const validCaptions = (captions ?? []).filter((c) => c.caption?.filename);

  const handlePlay = (event: SyntheticEvent<HTMLVideoElement>) => {
    if (!analyticsSessionRef.current) return;

    analyticsSessionRef.current = handleSessionPlay(
      analyticsSessionRef.current,
      event,
      t("Video.introduction"),
      chapters
    );
  };

  const handleTimeUpdate = (event: SyntheticEvent<HTMLVideoElement>) => {
    if (!analyticsSessionRef.current) return;

    analyticsSessionRef.current = handleSessionTimeUpdate(
      analyticsSessionRef.current,
      event
    );
  };

  const handleSeeking = () => {
    if (!analyticsSessionRef.current) return;

    // Set the isSeeking flag to true when the user starts seeking in the video.
    // This will prevent the playedSeconds from being updated during seeking.
    analyticsSessionRef.current.isSeeking = true;
  };

  const handleSeeked = () => {
    if (!analyticsSessionRef.current) return;

    // Reset the isSeeking flag to false when the user finishes seeking in the video.
    // This will allow the playedSeconds to be updated again during normal playback.
    analyticsSessionRef.current.isSeeking = false;
  };

  if (!video?.filename) {
    return null;
  }

  return (
    <div className="relative my-14 aspect-video w-full">
      <player.Provider>
        <VideoPlayerSkin
          poster={poster?.filename}
          posterAlt={poster?.alt ?? title}
          chapters={chapters}
        >
          <Video
            src={video.filename}
            playsInline
            onPlay={handlePlay}
            onTimeUpdate={handleTimeUpdate}
            onSeeking={handleSeeking}
            onSeeked={handleSeeked}
          >
            {validCaptions.map((c) => (
              <CaptionTrack
                key={c._uid}
                assetUrl={c.caption.filename}
                language={c.language}
              />
            ))}
          </Video>
          <GoogleCast />
        </VideoPlayerSkin>
      </player.Provider>
    </div>
  );
}
